import { node, nodeWithArgs, toMutation, toQuery } from 'common/util/queryHelper';
import { mutateGraphQL, queryGraphQL } from 'common/util/query';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { AuthorizationScheme, WebServiceType } from 'common/enums/apiConfiguration';

export const API_INTEGRATIONS_START_LOADING = 'API_INTEGRATIONS_START_LOADING';
export const API_INTEGRATIONS_END_LOADING = 'API_INTEGRATIONS_END_LOADING';
export const API_INTEGRATIONS_LOAD_SUCCESS = 'API_INTEGRATIONS_LOAD_SUCCESS';
export const API_INTEGRATIONS_CLEAN = 'API_INTEGRATION_CLEAN';
export const API_INTEGRATION_TOGGLE_SUCCESS = 'API_INTEGRATION_TOGGLE_SUCCESS';
export const SET_SELECTED_INTEGRATION_MAPPING_ID = 'SET_SELECTED_INTEGRATION_MAPPING_ID';
export const LOAD_INTEGRATION_DETAIL_SUCCESS = 'LOAD_INTEGRATION_DETAIL_SUCCESS';
export const SAVE_INTEGRATION_API_CONFIG_SUCCESS = 'SAVE_INTEGRATION_API_CONFIG_SUCCESS';
export const SAVE_INTEGRATION_COMPLION_AUTH_CLIENT_ID_SUCCESS = 'SAVE_INTEGRATION_COMPLION_AUTH_CLIENT_ID_SUCCESS';
export const CLEAN_SELECTED_INTEGRATION_MAPPING_ID = 'CLEAN_SELECTED_INTEGRATION_MAPPING_ID';

export const fetchApiIntegrations = (institutionId) => async (dispatch) => {
    dispatch(startApiIntegrationsLoadingAction());
    const institutionIntegrationsQuery = nodeWithArgs('integrations', {
        institutionId
    }, [
        'id',
        'mappingId',
        'name',
        'enabled'
    ]);

    const {
        errors,
        data: {
            integrations
        }
    } = await queryGraphQL(toQuery(institutionIntegrationsQuery));
    dispatch(endApiIntegrationsLoadingAction());
    if (errors) {
        dispatch(addApplicationMessage('error', 'Failed to fetch API integrations.'));
        return;
    }
    dispatch(loadApiIntegrationsSuccessAction(integrations));
};

export const cleanApiIntegrations = () => dispatch => {
    dispatch(cleanApiIntegrationsAction());
};

export const toggleIntegration = ({ institutionId, integrationId, enabled }) => async (dispatch) => {
    const toggleInstitutionApiIntegrationQuery = nodeWithArgs('updateInstitutionIntegrationEnabledStatus', {
        institutionId,
        integrationId,
        enabled,
    }, [
        'id',
        'name',
        'enabled'
    ]);

    const {
        errors,
        data: {
            updateInstitutionIntegrationEnabledStatus: apiIntegration
        }
    } = await mutateGraphQL(toMutation(toggleInstitutionApiIntegrationQuery));

    if (errors) {
        dispatch(addApplicationMessage('error', 'Failed to toggle on/off the integration.'));
        return;
    }
    const successMessage = apiIntegration.enabled
        ? `Api integration ${apiIntegration.name} was enabled`
        : 'Changing this status to Disabled does NOT remove the meta data field from the existing document types';
    dispatch(addApplicationMessage('success', successMessage));
    dispatch(toggleApiIntegrationSuccessAction(apiIntegration));
};

export const fetchIntegrationDetail = integrationMappingId => async (dispatch) => {
    dispatch(setSelectedIntegrationMappingIdAction(integrationMappingId));
    const integrationDetailQuery = nodeWithArgs('integrationConnection', {
        mappingId: integrationMappingId
    }, [
        'id',
        'mappingId',
        'name',
        'authorizationScheme',
        node('partnerApisConfig', [
            'tokenUrl',
            'postDocumentUrl',
            'postEventUrl',
            'apiKey',
            'clientId',
            'secret',
            'audience',
            'username',
            'password',
            'postDocumentUrl',
            'putDocumentUrl',
            'ftpServer',
            'ftpPort',
            'eventMountPath',
            'grantType',
            'scope'
        ]),
        'complionAuth0ClientId',
        'webServiceType'
    ]);

    const {
        errors,
        data: {
            integrationConnection: integrationDetail
        }
    } = await queryGraphQL(toQuery(integrationDetailQuery));
    if (errors) {
        dispatch(addApplicationMessage('error', 'Failed to fetch detail of a mapped integration.'));
        return;
    }
    dispatch(loadIntegrationDetailSuccessAction(integrationDetail));
};

export const saveIntegrationApiConfig  = ({
    integrationMappingId,
    tokenUrl,
    clientId,
    secret,
    audience,
    webServiceType,
    authorizationScheme,
    apiKey,
    username,
    password,
    postDocumentUrl,
    putDocumentUrl,
    ftpServer,
    ftpPort,
    eventMountPath,
    grantType,
    scope
}) => async (dispatch) => {
    const saveIntegrationApiConfigQuery = nodeWithArgs('updateIntegrationPartnerAPI', {
        mappingId: integrationMappingId,
        input: {
            tokenUrl,
            clientId,
            secret,
            audience,
            webServiceType,
            authorizationScheme,
            apiKey,
            username,
            password,
            postDocumentUrl,
            putDocumentUrl,
            ftpServer,
            ftpPort,
            eventMountPath,
            grantType,
            scope
        }
    }, [
        'id',
        'mappingId',
        'name',
        'authorizationScheme',
        node('partnerApisConfig', [
            'tokenUrl',
            'postDocumentUrl',
            'postEventUrl',
            'apiKey',
            'clientId',
            'secret',
            'audience',
            'username',
            'password',
            'postDocumentUrl',
            'putDocumentUrl',
            'ftpServer',
            'ftpPort',
            'eventMountPath',
            'grantType',
            'scope'
        ]),
        'complionAuth0ClientId',
        'webServiceType'
    ]);
    
    const {
        errors,
        data: {
            updateIntegrationPartnerAPI: integrationDetail
        }
    } = await mutateGraphQL(toMutation(saveIntegrationApiConfigQuery));
    if (errors) {
        dispatch(addApplicationMessage('error', 'Failed to save partner API config.'));
        return;
    }
    dispatch(addApplicationMessage('success', 'Save partner API config successfully.'));
    dispatch(saveIntegrationApiConfigSuccessAction(integrationDetail));
};

export const saveIntegrationComplionAuthClientId = ({
    integrationMappingId,
    complionAuth0ClientId
}) => async (dispatch) => {
    const saveIntegrationComplionAuthClientIdQuery = nodeWithArgs('updateIntegrationPublicAPI', {
        mappingId: integrationMappingId,
        input: {
            complionAuth0ClientId
        }
    }, [
        'id',
        'mappingId',
        'name',
        'authorizationScheme',
        node('partnerApisConfig', [
            'tokenUrl',
            'postDocumentUrl',
            'postEventUrl',
            'apiKey',
            'clientId',
            'secret',
            'audience',
            'username',
            'password',
            'postDocumentUrl',
            'putDocumentUrl',
            'ftpServer',
            'ftpPort',
            'eventMountPath',
            'grantType',
            'scope'
        ]),
        'complionAuth0ClientId',
        'webServiceType'
    ]);
    
    const {
        errors,
        data: {
            updateIntegrationPublicAPI: integrationDetail
        }
    } = await mutateGraphQL(toMutation(saveIntegrationComplionAuthClientIdQuery));
    if (errors) {
        dispatch(addApplicationMessage('error', 'Failed to save public API config.'));
        return;
    }
    dispatch(addApplicationMessage('success', 'Save public API config successfully.'));
    dispatch(saveIntegrationComplionAuthClientIdSuccessAction(integrationDetail));
};

export const testConnection = ({
    postDocumentUrl,
    putDocumentUrl,
    authorizationScheme,
    tokenUrl,
    clientId,
    secret,
    audience,
    apiKey,
    webServiceType,
    username,
    password,
    ftpServer,
    eventMountPath,
    ftpPort,
    grantType,
    scope
}) => async (dispatch) => {
    const test_failed = 'Connection Failed - please refresh the page and fill in the correct details.';
    const test_succeeded = 'Connection Successful.';

    if (authorizationScheme === AuthorizationScheme.OAUTH2) {
        const testConnectionQuery = nodeWithArgs('testOauth2Connection', {
            tokenUrl,
            grantType,
            clientId,
            clientSecret: secret,
            audience,
            scope,
            username,
            password,
        }, [
            'succeeded',
            'error'
        ]);
        const {
            data: {
                testOauth2Connection: {
                    succeeded
                }
            }
        } = await queryGraphQL(toQuery(testConnectionQuery));
        if (succeeded) {
            dispatch(addApplicationMessage('success', test_succeeded));
        } else {
            dispatch(addApplicationMessage('error', test_failed));
        }
    } else if (authorizationScheme === AuthorizationScheme.BASIC_AUTH && webServiceType === WebServiceType.SFTP) {
        // Do something to test SFTP connection
        const testConnectionQuery = nodeWithArgs('testSftpConnection', {
            ftpServer,
            eventMountPath,
            ftpPort,
            username,
            password,
        }, [
            'succeeded',
            'error',
        ]);
        const {
            data: {
                testSftpConnection: {
                    succeeded
                }
            }
        } = await queryGraphQL(toQuery(testConnectionQuery));
        if (succeeded) {
            dispatch(addApplicationMessage('success', test_succeeded));
        } else {
            dispatch(addApplicationMessage('error', test_failed));
        }
    } else if (authorizationScheme === AuthorizationScheme.BASIC_AUTH) {
        const testConnectionQuery = nodeWithArgs('testHTTPAuthBasicConnection', {
            url: postDocumentUrl,
            username,
            password,
        }, [
            'succeeded',
            'error'
        ]);
        const {
            data: {
                testHTTPAuthBasicConnection: {
                    succeeded
                }
            }
        } = await queryGraphQL(toQuery(testConnectionQuery));
        if (succeeded) {
            dispatch(addApplicationMessage('success', test_succeeded));
        } else {
            dispatch(addApplicationMessage('error', test_failed));
        }
    } else {
        dispatch(addApplicationMessage('error', `Does not support testing authorization scheme: ${authorizationScheme}`));
    }
};

export const cleanSelectedIntegrationMappingId = () => dispatch => {
    dispatch(cleanSelectedIntegrationMappingIdAction());
};

export const startApiIntegrationsLoadingAction = () => ({
    type: API_INTEGRATIONS_START_LOADING,
    payload: true
});

export const endApiIntegrationsLoadingAction = () => ({
    type: API_INTEGRATIONS_END_LOADING,
    payload: false
});

export const loadApiIntegrationsSuccessAction = apiIntegrations => ({
    type: API_INTEGRATIONS_LOAD_SUCCESS,
    payload: apiIntegrations
});

export const cleanApiIntegrationsAction = () => ({
    type: API_INTEGRATIONS_CLEAN
});

export const toggleApiIntegrationSuccessAction = apiIntegration => ({
    type: API_INTEGRATION_TOGGLE_SUCCESS,
    payload: apiIntegration
});

export const setSelectedIntegrationMappingIdAction = integrationMappingId => ({
    type: SET_SELECTED_INTEGRATION_MAPPING_ID,
    payload: integrationMappingId
});

export const loadIntegrationDetailSuccessAction = integrationDetail => ({
    type: LOAD_INTEGRATION_DETAIL_SUCCESS,
    payload: integrationDetail
});

export const saveIntegrationApiConfigSuccessAction = integrationDetail => ({
    type: SAVE_INTEGRATION_API_CONFIG_SUCCESS,
    payload: integrationDetail
});

export const saveIntegrationComplionAuthClientIdSuccessAction = integrationDetail => ({
    type: SAVE_INTEGRATION_COMPLION_AUTH_CLIENT_ID_SUCCESS,
    payload: integrationDetail
});

export const cleanSelectedIntegrationMappingIdAction = () => ({
    type: CLEAN_SELECTED_INTEGRATION_MAPPING_ID
});
